import React, { useState } from 'react';
import styled from 'styled-components';
import { CommonButton } from '../../../Styles/CommonStyles';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import nameStringFn from '../../../data/nameStringFn';
import { useUpdateComplaintsMutation } from '../../../features/complaints/complaintsApiSlice';
import { useGetSupportUsersQuery } from '../../../features/supportusers/supportusersApiSlice';

const FormContainer = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  margin-top: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  width: 100%;
`;

const FormTitle = styled.h3`
  margin-bottom: 20px;
  color: #1976d2;
  font-size: 1.25rem;
`;

const FormRow = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  align-items: flex-start;
`;

const FormField = styled.div`
  flex: 1;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;

const Button = styled(CommonButton)`
  padding: 8px 16px;
  min-width: 120px;
`;

const SubmitButton = styled(Button)`
  background-color: #4caf50;
`;

const CancelButton = styled(Button)`
  background-color: #f44336;
`;

const SupportStaffComplaintForm = ({ 
  row, 
  remark, 
  setRemark, 
  auth, 
  user_current_office, 
  getSingleuser, 
  supportusers,
  handleBack, 
  availableActions 
}) => {
  const [selectedAction, setSelectedAction] = useState('');
  const [loading, setLoading] = useState(false);
  
  const [updateComplaint] = useUpdateComplaintsMutation();

  const handleActionSubmit = async () => {
    if (!selectedAction) {
      alert('Please select an action');
      return;
    }

    if (!remark.trim()) {
      alert('Please enter a remark');
      return;
    }

    setLoading(true);
    
    try {
      const actionData = availableActions.find(action => action.code === selectedAction);
      const nextAssignee = getNextAssignee(actionData);
      
      const updateData = {
        id: row._id,
        req_data_entry: {
          action: actionData.action,
          sent_to_tag: actionData.sent_to_tag,
          text: remark,
          sent_by: {
            id: auth.id,
            name: supportusers[auth.id]?.name || {},
            designation: 'Support Staff'
          },
          sent_to: nextAssignee,
          time_stamp: new Date().toISOString()
        },
        curr_status: actionData.updatedStatus,
        curr_sent_by: auth.id,
        curr_sent_to: nextAssignee?.id || auth.id,
        curr_sent_to_string: nextAssignee ? `${nameStringFn(nextAssignee)}- ${nextAssignee.designation}`.trim() : 'Support Staff',
        curr_sent_to_tag: actionData.sent_to_tag,
        opened: false
      };
      console.log('Update Data:', updateData);
      const res = await updateComplaint(updateData).unwrap();
      console.log(res)
      alert('Action completed successfully');
      handleBack();
    } catch (error) {
      console.error('Error updating complaint:', error);
      alert('Failed to submit action. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  const getNextAssignee = (actionData) => {
    switch (actionData.code) {
      case 'SRCLSD': {
        // Send to Supervisor for closure
        const supervisor = supportusers
          ? Object.values(supportusers).find(user => user.roles?.includes('Supervisor'))
          : null;
        return supervisor ? {
          id: supervisor._id || supervisor.id,
          name: supervisor.name || {},
          designation: 'Supervisor'
        } : null;
      }
      
      case 'POH':
        return {
          id: auth.id,
          name: supportusers[auth.id].name || {},
          designation: 'Support Staff'
        };
      
      default:
        return null;
    }
  };

  return (
    <FormContainer>
      <FormTitle>Support Staff Actions</FormTitle>
      
      <FormRow>
        <FormField>
          <FormControl fullWidth>
            <InputLabel>Select Action</InputLabel>
            <Select
              value={selectedAction}
              onChange={(e) => setSelectedAction(e.target.value)}
              label="Select Action"
            >
              {availableActions.map((action) => (
                <MenuItem key={action.code} value={action.code}>
                  {action.action}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </FormField>
      </FormRow>

      <FormRow>
        <FormField>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Remarks"
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            placeholder="Enter your remarks here..."
            variant="outlined"
          />
        </FormField>
      </FormRow>

      <ButtonsWrapper>
        <SubmitButton
          onClick={handleActionSubmit}
          disabled={loading || !selectedAction || !remark.trim()}
        >
          {loading ? 'Submitting...' : 'Submit Action'}
        </SubmitButton>
        <CancelButton onClick={handleBack}>
          Cancel
        </CancelButton>
      </ButtonsWrapper>
    </FormContainer>
  );
};

export default SupportStaffComplaintForm; 